import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const API_URL = window.location.origin;

const BirthdayPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [isBirthday, setIsBirthday] = useState(false);

  useEffect(() => {
    checkBirthdayStatus();
  }, []);

  const checkBirthdayStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/birthday-status`);
      const { is_birthday, is_birthday_month, popup_shown, first_name } = response.data;
      
      // Show popup if it's their birthday month and popup hasn't been shown this year
      if ((is_birthday || is_birthday_month) && !popup_shown) {
        setFirstName(first_name || 'Sister');
        setIsBirthday(is_birthday);
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error checking birthday status:', error);
    }
  };

  const handleClose = async () => {
    setShowPopup(false);
    
    // Mark popup as shown
    try {
      await axios.post(`${API_URL}/api/client/birthday-popup-shown`);
    } catch (error) {
      console.error('Error marking popup shown:', error);
    }
  };

  if (!showPopup) return null;

  return (
    <Dialog open={showPopup} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-0 bg-transparent">
        <div className="relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
          
          {/* Birthday Card */}
          <div className="bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100 rounded-2xl overflow-hidden shadow-2xl">
            {/* Confetti Header */}
            <div className="relative bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 py-8 px-6 text-center">
              <div className="absolute inset-0 overflow-hidden">
                {/* Animated confetti dots */}
                <div className="absolute top-2 left-[10%] w-3 h-3 bg-yellow-300 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                <div className="absolute top-4 left-[30%] w-2 h-2 bg-green-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="absolute top-1 left-[50%] w-4 h-4 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                <div className="absolute top-3 left-[70%] w-2 h-2 bg-pink-200 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="absolute top-2 left-[90%] w-3 h-3 bg-purple-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
              
              <p className="text-5xl mb-3 animate-bounce">🎂</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-md">
                {isBirthday ? 'Happy Birthday!' : 'Happy Birthday Month!'}
              </h2>
            </div>
            
            {/* Content */}
            <div className="p-6 text-center">
              <p className="text-xl text-forest font-semibold mb-4">
                {firstName}! 🎉✨
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {isBirthday 
                  ? "On this special day, your entire Sister Diamonds family celebrates YOU! May this year bring abundant blessings, unexpected joys, and the courage to step into your greatest dreams."
                  : "Your Sister Diamonds family is celebrating you all month long! This is YOUR month to shine, dream big, and embrace all the blessings coming your way."
                }
              </p>
              
              {/* Quote */}
              <div className="bg-white/60 rounded-xl p-4 mb-6 border-l-4 border-pink-400">
                <p className="text-gray-600 italic text-sm">
                  "You are a rare diamond, and the world is brighter because of you."
                </p>
                <p className="text-pink-600 text-xs mt-2 font-medium">
                  — Crystal & Dr. Nicole 💎
                </p>
              </div>
              
              {/* Celebration emojis */}
              <div className="flex justify-center gap-2 text-2xl mb-4">
                <span className="animate-pulse">🎈</span>
                <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>🎊</span>
                <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>🎁</span>
                <span className="animate-pulse" style={{ animationDelay: '0.6s' }}>💝</span>
                <span className="animate-pulse" style={{ animationDelay: '0.8s' }}>🌟</span>
              </div>
              
              <Button 
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold py-3 rounded-xl shadow-lg transition-all hover:scale-[1.02]"
              >
                Thank You! 🥰
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BirthdayPopup;
